/**
 * Placement maths for the @mention menu shown by the comment composers.
 *
 * Lives outside the component so both composers (side-panel and the floating
 * anchored one) share one behaviour, and so the collision handling is unit
 * testable without mounting TipTap.
 */

/**
 * Menu box metrics. `MENTION_MENU_WIDTH` must stay in sync with the popup's
 * `w-56`; the height is owned here — the popup applies the computed `maxHeight`
 * inline rather than a Tailwind cap, so this constant is the only source of it.
 */
export const MENTION_MENU_WIDTH = 224
export const MENTION_MENU_MAX_HEIGHT = 224
const MENTION_MENU_MIN_HEIGHT = 96
const VIEWPORT_GAP = 8
const CARET_GAP = 4

export interface MentionPopupPlacement {
  top: number
  left: number
  maxHeight: number
}

export interface MentionPopupAnchor {
  caretTop: number
  caretBottom: number
  caretLeft: number
  viewportWidth: number
  viewportHeight: number
}

/**
 * Collision-aware placement for the @mention menu.
 *
 * Anchoring naively to the caret clips the menu off the right edge when the
 * `@` is typed near the end of a line, and off the bottom when the composer
 * sits low in the viewport (the anchored composer often does). Clamp
 * horizontally, and flip above the caret when the space below cannot hold a
 * usable menu.
 */
export function computeMentionPopupPosition({
  caretTop,
  caretBottom,
  caretLeft,
  viewportWidth,
  viewportHeight,
}: MentionPopupAnchor): MentionPopupPlacement {
  const maxLeft = viewportWidth - MENTION_MENU_WIDTH - VIEWPORT_GAP
  const left =
    maxLeft < VIEWPORT_GAP
      ? Math.max(0, maxLeft)
      : Math.min(Math.max(caretLeft, VIEWPORT_GAP), maxLeft)

  const spaceBelow = viewportHeight - (caretBottom + CARET_GAP) - VIEWPORT_GAP
  const spaceAbove = caretTop - CARET_GAP - VIEWPORT_GAP

  const fitsBelow = spaceBelow >= MENTION_MENU_MIN_HEIGHT
  if (fitsBelow || spaceBelow >= spaceAbove) {
    const maxHeight = Math.max(1, Math.min(MENTION_MENU_MAX_HEIGHT, spaceBelow))
    return { top: Math.max(0, caretBottom + CARET_GAP), left, maxHeight }
  }

  const maxHeight = Math.max(1, Math.min(MENTION_MENU_MAX_HEIGHT, spaceAbove))
  return { top: Math.max(0, caretTop - CARET_GAP - maxHeight), left, maxHeight }
}
